import React from 'react'
import { getUserAnalytics } from '../../api/user'
import { useQuery } from '@tanstack/react-query'
import { snakeToCamel } from '../AdminDashboard/AdminEditRelease'

const CurrentStreamCard = ({ userData }) => {
  const uid = localStorage.getItem('uid')

  // Music icon SVG component
  const MusicIcon = () => (
    <svg 
      width="32" 
      height="32" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <circle cx="8" cy="18" r="4"/>
      <path d="M12 18V2l7 4"/>
    </svg>
  );

  // Get analytics data
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['userAnalytics', uid],
    queryFn: () => getUserAnalytics(uid),
    select: (data) => {
      if (!data) return null;

      const reportsData = data.reports?.map(report => snakeToCamel(report)) || [];
      const sortingData = data.sorting?.map(channel => snakeToCamel(channel)) || [];

      // Calculate total streams from platforms
      let totalStreams = 0;
      let platformStats = [];
      
      if (sortingData && sortingData.length > 0) {
        const channels = sortingData[0].channels || [];
        
        // Calculate total streams and prepare platform data
        channels.forEach(channel => {
          totalStreams += channel.streams || 0;
          
          let platformName = channel.channel?.toLowerCase() || channel.platform?.toLowerCase();
          
          // Normalize platform names
          if (platformName === 'snapshat') platformName = 'Snap';
          else if (platformName === 'apple music / itunes' || platformName === 'itunes' || platformName === 'itunes match') platformName = 'Apple Music';
          else if (platformName?.includes('facebook')) platformName = 'Facebook';
          else if (platformName?.includes('amazon')) platformName = 'Amazon';
          else if (platformName?.includes('tiktok')) platformName = 'TikTok';
          else if (platformName?.includes('spotify')) platformName = 'Spotify';
          else platformName = channel.channel || channel.platform;

          platformStats.push({
            name: platformName,
            streams: `${(channel.streams || 0).toLocaleString()} Streams`
          });
        });
      }

      // Sort platforms by streams (extract number for sorting)
      platformStats.sort((a, b) => {
        const aStreams = parseInt(a.streams.replace(/[^\d]/g, '')) || 0;
        const bStreams = parseInt(b.streams.replace(/[^\d]/g, '')) || 0;
        return bStreams - aStreams;
      });

      // Get latest month revenue for goal calculation
      const latestRevenue = reportsData.length > 0 ? reportsData[reportsData.length - 1]?.totalPrice || 0 : 0;
      
      return {
        totalStreams,
        platformStats: platformStats.slice(0, 4), // Top 4 platforms
        currentRevenue: latestRevenue,
        balance: data.balance || 0
      };
    }
  });

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k`;
    }
    return num.toString();
  };

  // Calculate next goal
  const calculateNextStreamingGoal = (currentStreams) => {
    if (currentStreams <= 0) return 50000;

    const currentM = currentStreams / 1000000;
    const milestones = [1, 2, 5, 10, 15, 20, 25, 50, 75, 100];

    for (const milestone of milestones) {
      if (currentM < milestone) {
        const proximity = (milestone - currentM) / currentM;
        if (proximity < 0.25 && milestone * 1000000 > currentStreams) {
          return milestone * 1000000;
        }
        break;
      }
    }

    let goal = currentStreams * 1.25;
    let roundingIncrement;

    if (goal < 100000) {
      roundingIncrement = 50000; // For goals under 100k, round to nearest 50k
    } else if (goal < 1000000) {
      roundingIncrement = 100000; // For goals under 1M, round to nearest 100k
    } else if (goal < 10000000) {
      roundingIncrement = 500000; // For goals under 10M, round to nearest 0.5M
    } else {
      roundingIncrement = 1000000; // For goals above 10M, round to nearest 1M
    }

    let nextGoal = Math.ceil(goal / roundingIncrement) * roundingIncrement;

    if (nextGoal <= currentStreams) {
      nextGoal += roundingIncrement;
    }

    return nextGoal;
  };

  const currentStreams = analyticsData?.totalStreams || 0;
  const nextGoal = calculateNextStreamingGoal(currentStreams);

  return (
    <div className="stream-section">
      <div className="current-stream-card">
        {/* Current Stream Section */}
        <div className="stream-header">
          <div className="stream-info">
            <h2 className="section-title">Current Stream</h2>
            <div className="stream-number">
              {isLoading ? '...' : formatNumber(currentStreams)}
            </div>
          </div>
          <div className="music-icon-container">
            <div className="music-icon-wrapper">
              <MusicIcon />
            </div>
            <div className="decoration-circle decoration-circle-1"></div>
            <div className="decoration-circle decoration-circle-2"></div>
          </div>
        </div>

        {/* Next Stream Goals Section */}
        <div className="next-goal-card">
          <h3 className="goal-title">Next Stream Goals</h3>
          <div className="goal-number">
            {isLoading ? '...' : formatNumber(nextGoal)}
          </div>
        </div>

        {/* Platform Statistics */}
        <div className="platform-stats">
          <div className="platform-column">
            {isLoading ? (
              <>
                <div className="platform-item">
                  <div className="platform-name">Loading...</div>
                  <div className="platform-streams">...</div>
                </div>
                <div className="platform-item">
                  <div className="platform-name">Loading...</div>
                  <div className="platform-streams">...</div>
                </div>
              </>
            ) : (
              analyticsData?.platformStats?.slice(0, 2).map((platform, index) => (
                <div key={index} className="platform-item">
                  <div className="platform-name">{platform.name}</div>
                  <div className="platform-streams">{platform.streams}</div>
                </div>
              ))
            )}
          </div>
          <div className="platform-column">
            {isLoading ? (
              <>
                <div className="platform-item">
                  <div className="platform-name">Loading...</div>
                  <div className="platform-streams">...</div>
                </div>
                <div className="platform-item">
                  <div className="platform-name">Loading...</div>
                  <div className="platform-streams">...</div>
                </div>
              </>
            ) : (
              analyticsData?.platformStats?.slice(2, 4).map((platform, index) => (
                <div key={index + 2} className="platform-item">
                  <div className="platform-name">{platform.name}</div>
                  <div className="platform-streams">{platform.streams}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .current-stream-card {
          padding: 24px;
          background: #f9fafb;
          border-radius: 16px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .stream-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .stream-number {
          font-size: 48px;
          font-weight: 700;
          background: linear-gradient(to right, #2563eb, #1e40af);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
        }

        .music-icon-container {
          position: relative;
        }

        .music-icon-wrapper {
          width: 64px;
          height: 64px;
          background: linear-gradient(to bottom right, #a855f7, #9333ea);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          transform: rotate(12deg);
          color: white;
        }

        .decoration-circle {
          position: absolute;
          border-radius: 50%;
        }

        .decoration-circle-1 {
          bottom: -8px;
          right: -8px;
          width: 24px;
          height: 24px;
          background: linear-gradient(to bottom right, #f472b6, #a855f7);
        }

        .decoration-circle-2 {
          top: -4px;
          left: -4px;
          width: 16px;
          height: 16px;
          background: linear-gradient(to bottom right, #c084fc, #a855f7);
        }

        .next-goal-card {
          background: linear-gradient(to right, #334155, #1e293b);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .goal-title {
          color: white;
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .goal-number {
          color: #cbd5e1;
          font-size: 48px;
          font-weight: 700;
          line-height: 1;
        }

        .platform-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px 32px;
          flex: 1;
        }

        .platform-column {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .platform-item {
          display: flex;
          flex-direction: column;
        }

        .platform-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 2px;
        }

        .platform-streams {
          color: #4b5563;
          font-size: 14px;
        }

        @media (max-width: 640px) {
          .current-stream-card {
            max-width: 100%;
            padding: 20px;
          }
          
          .stream-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          
          .platform-stats {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>
    </div>
  )
}

export default CurrentStreamCard 